import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, Pill, Sunrise, Sun, Moon } from 'lucide-react';
import { toast } from 'sonner';
import { nexaApi } from './api';

const COLLAPSE_KEY = 'nexa_supplements_collapsed';

const CheckCircle = ({ done }) => (
  <motion.span
    animate={{
      backgroundColor: done ? '#38BDF8' : 'rgba(255,255,255,0)',
      borderColor: done ? '#38BDF8' : 'rgba(255,255,255,0.25)',
    }}
    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
    className="w-7 h-7 rounded-full border flex items-center justify-center shrink-0"
  >
    {done && (
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 600, damping: 20 }}
      >
        <Check className="w-4 h-4 text-black" strokeWidth={3} />
      </motion.span>
    )}
  </motion.span>
);

export const SupplementsCard = ({ supplements, onToggle }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const open = !collapsed;
  const toggleFold = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  if (!supplements || !supplements.items?.length) return null;
  const pct = supplements.total > 0
    ? Math.round((supplements.completed / supplements.total) * 100)
    : 0;
  return (
    <div data-testid="nexa-supplements-card">
      <button
        type="button"
        onClick={toggleFold}
        data-testid="nexa-supplements-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between mb-3 text-left"
      >
        <span className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-white/60">
          <motion.span
            animate={{ rotate: open ? 0 : -90 }}
            transition={{ type: 'spring', stiffness: 300, damping: 26 }}
            className="inline-flex text-sky-400"
          >
            <ChevronDown className="w-4 h-4" strokeWidth={2.5} />
          </motion.span>
          <Pill className="w-3.5 h-3.5 text-sky-400" strokeWidth={1.8} /> Supplements
        </span>
        <span className="text-sm text-sky-400 font-semibold" data-testid="nexa-supplements-progress">
          {supplements.completed}/{supplements.total}
        </span>
      </button>
      <div className="h-1 rounded-full bg-white/[0.06] mb-4 overflow-hidden">
        <motion.div
          animate={{ width: `${pct}%` }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
          className="h-full bg-sky-400 rounded-full"
        />
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] overflow-hidden" data-testid="nexa-supplements-list">
              {supplements.items.map((item, idx) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onToggle(item)}
                  data-testid={`nexa-supplements-item-${idx}`}
                  className={`w-full h-16 px-5 flex items-center gap-4 text-left bg-[#0A0A0A] ${
                    idx < supplements.items.length - 1 ? 'border-b border-white/[0.06]' : ''
                  }`}
                >
                  <CheckCircle done={item.done} />
                  <span
                    className={`flex-1 min-w-0 text-base transition-colors duration-200 ${
                      item.done ? 'text-white/35 line-through' : 'text-white'
                    }`}
                  >
                    {item.text}
                  </span>
                  {item.time && (
                    <span className="text-[11px] font-mono text-sky-400/80 shrink-0">{item.time}</span>
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const slotIcon = (time) => {
  const h = parseInt(time, 10) || 12;
  if (h < 9) return Sunrise;
  if (h >= 21) return Moon;
  return Sun;
};

export const SupplementsPlanCard = () => {
  const [data, setData] = useState(null);
  const load = () => nexaApi.get('/supplements').then(({ data: d }) => setData(d)).catch(() => {});
  useEffect(() => {
    load();
  }, []);
  if (!data || !data.supplements?.length) return null;
  const groups = [];
  data.supplements.forEach((s) => {
    const g = groups.find((x) => x.time === s.time);
    if (g) g.items.push(s);
    else groups.push({ time: s.time, items: [s] });
  });
  const taken = data.supplements.filter((s) => s.done).length;
  const toggle = async (s) => {
    setData((prev) => ({
      ...prev,
      supplements: prev.supplements.map((x) => (x.id === s.id ? { ...x, done: !x.done } : x)),
    }));
    try {
      await nexaApi.post('/routine/check', { item_id: `supp:${s.id}`, done: !s.done, routine_id: 'supplements' });
    } catch {
      toast.error('Could not save — check connection');
      load();
    }
  };
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="nexa-supplements-plan">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Pill className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
          <span className="text-xs tracking-[0.2em] uppercase text-white/40">Supplement protocol</span>
        </div>
        <span className="text-sm text-sky-400 font-semibold" data-testid="nexa-supplements-plan-progress">
          {taken}/{data.supplements.length} today
        </span>
      </div>
      <div className="flex flex-col gap-5">
        {groups.map((g) => {
          const Icon = slotIcon(g.time);
          return (
            <div key={g.time} data-testid={`nexa-supplements-slot-${g.time}`}>
              <div className="flex items-center gap-1.5 text-white/40 text-[10px] tracking-[0.15em] uppercase mb-2">
                <Icon className="w-3 h-3 text-sky-400/70" /> {g.time}
              </div>
              <div className="flex flex-col">
                {g.items.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => toggle(s)}
                    data-testid={`nexa-supplements-plan-item-${s.id}`}
                    className="flex items-center gap-3 py-2 text-left"
                  >
                    <CheckCircle done={s.done} />
                    <span
                      className={`text-sm transition-colors duration-200 ${
                        s.done ? 'text-white/35 line-through' : 'text-white/90'
                      }`}
                    >
                      {s.name}
                      {s.dose && <span className="text-white/40"> — {s.dose}</span>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-white/30 mt-4 pt-3 border-t border-white/[0.06]">
        06:00 doses live in your morning routine, 22:00 in your evening routine. Tell NEXA in chat
        to add, remove or change a supplement.
      </p>
    </div>
  );
};
